import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Animated,
  Dimensions,
  Platform,
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { useAuth } from '../hooks/useAuth';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

if (isWeb && typeof document !== 'undefined') {
  document.title = 'Welcome to SheetShed';
}

// ---- Confetti particle (pure RN Animated) ----
const COLORS = ['#C75B7A', '#F59E0B', '#8B5CF6', '#10B981', '#3B82F6', '#EC4899'];

const ConfettiPiece = ({ delay, x, color }) => {
  const translateY = useRef(new Animated.Value(-20)).current;
  const translateX = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const rotate = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const drift = (Math.random() - 0.5) * 120;
    setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }),
        Animated.timing(translateY, { toValue: 500 + Math.random() * 200, duration: 2000 + Math.random() * 1000, useNativeDriver: true }),
        Animated.timing(translateX, { toValue: drift, duration: 2200 + Math.random() * 800, useNativeDriver: true }),
        Animated.timing(rotate, { toValue: 6 + Math.random() * 6, duration: 2000, useNativeDriver: true }),
        Animated.sequence([
          Animated.delay(1400),
          Animated.timing(opacity, { toValue: 0, duration: 600, useNativeDriver: true }),
        ]),
      ]).start();
    }, delay);
  }, []);

  const spin = rotate.interpolate({ inputRange: [0, 12], outputRange: ['0deg', '720deg'] });

  return (
    <Animated.View
      style={[
        styles.confetti,
        {
          left: x,
          backgroundColor: color,
          transform: [{ translateY }, { translateX }, { rotate: spin }],
          opacity,
        },
      ]}
    />
  );
};

// ---- Floating animated section ----
const FadeIn = ({ delay = 0, children, style }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(24)).current;
  useEffect(() => {
    setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 10, tension: 60, useNativeDriver: true }),
      ]).start();
    }, delay);
  }, []);
  return <Animated.View style={[style, { opacity, transform: [{ translateY }] }]}>{children}</Animated.View>;
};

// ---- Check icon ----
const CheckIcon = ({ color }) => {
  if (!isWeb) return <Text style={{ color, fontSize: 14 }}>✓</Text>;
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );
};

// ---- Crown icon ----
const CrownIcon = ({ color, size = 48 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>♛</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M2 19h20v2H2v-2zM2 17l4-9 6 4 4-6 4 6-4 5H2v0z" />
    </svg>
  );
};

export const WelcomeScreen = ({ navigation, route }) => {
  const { user } = useAuth();
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const [isDark] = useState(() => isWeb && localStorage?.getItem('sheetshed_darkmode') === 'true');

  const theme = isDark ? baseTheme.dark : baseTheme.light;
  const isMobile = screenWidth < 640;

  const tier = user?.subscription?.tier || route?.params?.tier || 'lifetime';
  const isLifetime = tier === 'lifetime';

  useEffect(() => {
    if (!isWeb) return;
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Generate confetti
  const confettiPieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    delay: Math.random() * 1000,
    x: `${Math.random() * 100}%`,
    color: COLORS[i % COLORS.length],
  }));

  const perks = isLifetime
    ? ['Unlimited sheet storage', 'Unlimited sharing', 'All future updates', 'Priority support', 'Founding member badge']
    : ['Unlimited sheet storage', '10 share credits / month (roll over)', 'All pro features', 'Priority support'];

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Confetti */}
      <View style={styles.confettiContainer} pointerEvents="none">
        {confettiPieces.map(p => (
          <ConfettiPiece key={p.id} delay={p.delay} x={p.x} color={p.color} />
        ))}
      </View>

      <View style={[styles.inner, { padding: isMobile ? 24 : 60 }]}>
        {/* Icon */}
        <FadeIn delay={200} style={styles.iconWrap}>
          <View style={[styles.iconCircle, { backgroundColor: isLifetime ? '#F59E0B20' : theme.primary + '18' }]}>
            <CrownIcon color={isLifetime ? '#F59E0B' : theme.primary} size={isMobile ? 40 : 52} />
          </View>
        </FadeIn>

        {/* Heading */}
        <FadeIn delay={350}>
          <Text style={[styles.heading, { color: theme.textPrimary, fontSize: isMobile ? 30 : 44 }]}>
            You're in! Welcome to SheetShed {isLifetime ? 'Lifetime' : 'Pro'}.
          </Text>
        </FadeIn>

        <FadeIn delay={500}>
          <Text style={[styles.subheading, { color: theme.textSecondary, fontSize: isMobile ? 16 : 19 }]}>
            {isLifetime
              ? 'One payment, yours forever. Here\'s everything you now have access to:'
              : 'Your subscription is active. Here\'s what\'s included every month:'}
          </Text>
        </FadeIn>

        {/* Perks */}
        <FadeIn delay={650} style={styles.perksList}>
          {perks.map((p, i) => (
            <View key={i} style={styles.perkRow}>
              <View style={[styles.checkCircle, { backgroundColor: theme.primary + '20' }]}>
                <CheckIcon color={theme.primary} />
              </View>
              <Text style={[styles.perkText, { color: theme.textPrimary, fontSize: isMobile ? 15 : 17 }]}>{p}</Text>
            </View>
          ))}
        </FadeIn>

        {/* CTA */}
        <FadeIn delay={900} style={styles.ctaWrap}>
          <Pressable
            style={[styles.ctaBtn, { backgroundColor: theme.primary }]}
            onPress={() => navigation.replace('Library')}
          >
            <Text style={[styles.ctaBtnText, { fontSize: isMobile ? 16 : 18 }]}>Go to my Library →</Text>
          </Pressable>
          {!isLifetime && (
            <Pressable onPress={() => navigation.replace('Settings')}>
              <Text style={[styles.manageLink, { color: theme.textMuted, fontSize: 14 }]}>Manage subscription in Settings</Text>
            </Pressable>
          )}
        </FadeIn>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, overflow: 'hidden' },
  confettiContainer: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 },
  confetti: { position: 'absolute', top: 0, width: 10, height: 14, borderRadius: 2 },
  inner: { flex: 1, justifyContent: 'center', alignItems: 'center', zIndex: 1 },
  iconWrap: { marginBottom: 28 },
  iconCircle: { width: 100, height: 100, borderRadius: 50, justifyContent: 'center', alignItems: 'center' },
  heading: { fontWeight: '800', textAlign: 'center', marginBottom: 16, maxWidth: 600, lineHeight: 52 },
  subheading: { textAlign: 'center', marginBottom: 36, maxWidth: 500, lineHeight: 28 },
  perksList: { width: '100%', maxWidth: 440, marginBottom: 48 },
  perkRow: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 14 },
  checkCircle: { width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  perkText: { flex: 1, fontWeight: '500' },
  ctaWrap: { alignItems: 'center', gap: 16 },
  ctaBtn: { paddingHorizontal: 40, paddingVertical: 18, borderRadius: 999 },
  ctaBtnText: { color: '#fff', fontWeight: '700' },
  manageLink: { marginTop: 4 },
});

export default WelcomeScreen;
